// src/pages/administrasi/KasMasuk.jsx
//
// Bendahara's Kas Masuk ledger — one row per inflow of funds for an event
// (realisasi dari Pengajuan Dana yang cair). Deliberately simple compared to
// Realisasi Nota: no OCR, just a manually-typed nominal plus a proof-of-
// transfer attachment (screenshot or PDF) — the amount is already known
// from the Pengajuan Dana that prompted it, so extracting it back out of a
// file would be pure overhead.
//
// Progress bar compares the sum of entries here against the event's total
// Anggaran (RAB) so Bendahara can see "sudah cair X dari Y" at a glance.
import { useEffect, useState, useCallback, useRef } from "react";
import { API_BASE_URL } from "../../utils/config";
import { responseErrorMessage } from "../../utils/apiError";
import CustomSelect from "../../components/CustomSelect";
import RupiahInput from "../../components/RupiahInput";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const isFormData = opts.body instanceof FormData;
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) throw new Error(await responseErrorMessage(res));
  return res.status === 204 ? null : res.json();
}

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const resolveUrl = (u) => (u && u.startsWith("/") ? `${API_BASE_URL}${u}` : u);

const emptyForm = {
  tanggal: "",
  nominal: "",
  sumber: "",
  keterangan: "",
};

export default function KasMasuk() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [items, setItems] = useState([]);
  const [rabTotal, setRabTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [lampiranFile, setLampiranFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [repushingId, setRepushingId] = useState(null);
  const [formError, setFormError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    apiFetch("/api/administrasi/events/?active_only=1")
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setEvents(list);
        if (list.length) setEventId(String(list[0].id));
      })
      .catch((e) => setError(e.message));
  }, []);

  const loadItems = useCallback(async () => {
    if (!eventId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`/api/administrasi/kas-masuk/?event_id=${eventId}`);
      setItems(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!eventId) {
      setRabTotal(0);
      return;
    }
    apiFetch(`/api/administrasi/rab-items/?event_id=${eventId}`)
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setRabTotal(list.reduce((sum, r) => sum + (r.total || 0), 0));
      })
      .catch(() => setRabTotal(0));
  }, [eventId]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setLampiranFile(null);
    setFormError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      tanggal: it.tanggal || "",
      nominal: String(it.nominal),
      sumber: it.sumber || "",
      keterangan: it.keterangan || "",
    });
    setLampiranFile(null);
    setFormError("");
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.nominal || Number(form.nominal) <= 0) {
      setFormError("Nominal wajib diisi.");
      return;
    }
    if (!editingId && !lampiranFile) {
      setFormError("Lampiran wajib dilampirkan.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await apiFetch(`/api/administrasi/kas-masuk/${editingId}/`, {
          method: "PATCH",
          body: JSON.stringify({
            tanggal: form.tanggal || null,
            nominal: Number(form.nominal) || 0,
            sumber: form.sumber.trim(),
            keterangan: form.keterangan.trim(),
          }),
        });
      } else {
        const body = new FormData();
        body.append("event_id", eventId);
        if (form.tanggal) body.append("tanggal", form.tanggal);
        body.append("nominal", String(Number(form.nominal) || 0));
        body.append("sumber", form.sumber.trim());
        body.append("keterangan", form.keterangan.trim());
        body.append("lampiran", lampiranFile);
        await apiFetch("/api/administrasi/kas-masuk/", { method: "POST", body });
      }
      setShowForm(false);
      resetForm();
      loadItems();
    } catch (err) {
      setFormError(err.message);
      // A failed POST may still have saved on the server (a gateway timeout cuts
      // the reply, not the save) — refresh so it shows up rather than being
      // re-entered as a duplicate.
      if (!editingId) loadItems();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Hapus entri "${it.sumber || "Kas Masuk"}"?`)) return;
    try {
      await apiFetch(`/api/administrasi/kas-masuk/${it.id}/`, { method: "DELETE" });
      loadItems();
    } catch (err) {
      alert(err.message);
    }
  };

  // Retries the Drive upload for an entry whose first upload failed — the
  // file is already stored on the server, so nothing is re-attached here.
  const handleRepush = async (it) => {
    setError("");
    setRepushingId(it.id);
    try {
      await apiFetch(`/api/administrasi/kas-masuk/${it.id}/repush-drive/`, { method: "POST" });
      loadItems();
    } catch (err) {
      setError(err.message);
    } finally {
      setRepushingId(null);
    }
  };

  const grandTotal = items.reduce((sum, it) => sum + it.nominal, 0);
  const progressPct = rabTotal > 0 ? Math.min(100, Math.round((grandTotal / rabTotal) * 100)) : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4">
        <h1 className="text-2xl font-semibold">Kas Masuk</h1>
        <button
          onClick={openCreate}
          disabled={!eventId}
          className="shrink-0 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
        >
          + Tambah Pemasukan
        </button>
      </div>

      <div className="glass-card p-4 mb-4">
        <label className="block text-xs font-medium mb-1">Event</label>
        <CustomSelect
          value={eventId}
          onChange={setEventId}
          options={events.map((ev) => ({ value: String(ev.id), label: ev.name }))}
          placeholder="-- Pilih event --"
        />
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading && <p className="text-sm text-muted mb-3">Loading...</p>}

      {rabTotal > 0 && (
        <div className="glass-card p-4 mb-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span>Sudah cair {fmtRp(grandTotal)} dari {fmtRp(rabTotal)} Anggaran</span>
            <span className="font-semibold">{progressPct}%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
            <div
              className="h-full bg-brand-600"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      <div className="glass-card p-4 mb-4">
        <div className="glass-table-wrap overflow-x-auto">
          <table className="glass-table text-xs">
            <thead>
              <tr>
                <th className="text-left">Tanggal</th>
                <th className="text-left">Sumber</th>
                <th className="text-left">Keterangan</th>
                <th className="text-right">Nominal</th>
                <th className="text-left">Lampiran</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t">
                  <td className="px-2 py-1.5 whitespace-nowrap">{it.tanggal || "-"}</td>
                  <td className="px-2 py-1.5">{it.sumber || "-"}</td>
                  <td className="px-2 py-1.5">{it.keterangan || "-"}</td>
                  <td className="px-2 py-1.5 text-right font-medium">{fmtRp(it.nominal)}</td>
                  <td className="px-2 py-1.5">
                    <a
                      href={it.drive_file_url || resolveUrl(it.lampiran_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 hover:underline"
                    >
                      Lihat
                    </a>
                    {!it.drive_file_url && (
                      <>
                        <span className="ml-1 text-amber-600" title="Belum terupload ke Drive">
                          ⚠
                        </span>
                        <button
                          onClick={() => handleRepush(it)}
                          disabled={repushingId === it.id}
                          className="ml-1 text-amber-600 hover:underline disabled:opacity-50"
                        >
                          {repushingId === it.id ? "Mengirim..." : "Push ulang"}
                        </button>
                      </>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(it)}
                      className="text-brand-600 hover:underline mr-2"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(it)}
                      className="text-red-500 hover:underline"
                    >
                      Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && items.length === 0 && (
          <p className="text-sm text-muted mt-3">Belum ada pemasukan untuk event ini.</p>
        )}
      </div>

      {items.length > 0 && (
        <div className="glass-card p-4 flex items-center justify-between">
          <span className="font-semibold">TOTAL KAS MASUK</span>
          <span className="font-semibold text-lg">{fmtRp(grandTotal)}</span>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="glass-card w-full max-w-lg p-6 my-8">
            <h2 className="text-lg font-semibold mb-4">
              {editingId ? "Edit Pemasukan" : "Tambah Pemasukan"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">Sumber</label>
                <input
                  type="text"
                  value={form.sumber}
                  onChange={(e) => setForm((f) => ({ ...f, sumber: e.target.value }))}
                  placeholder="mis. Bendahara Umum, Sponsor A"
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Tanggal</label>
                  <input
                    type="date"
                    value={form.tanggal}
                    onChange={(e) => setForm((f) => ({ ...f, tanggal: e.target.value }))}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Nominal</label>
                  <RupiahInput
                    value={form.nominal}
                    onChange={(v) => setForm((f) => ({ ...f, nominal: v }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Keterangan</label>
                <textarea
                  value={form.keterangan}
                  onChange={(e) => setForm((f) => ({ ...f, keterangan: e.target.value }))}
                  rows={2}
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                />
              </div>

              {!editingId && (
                <div>
                  <label className="block text-xs font-medium mb-1">Lampiran (bukti transfer / nota / invoice)</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setLampiranFile(e.target.files?.[0] || null)}
                    className="w-full text-sm"
                  />
                  <p className="text-xs text-muted mt-1">Boleh foto/screenshot transfer, foto nota, atau file PDF (mis. invoice).</p>
                </div>
              )}

              {formError && <p className="text-red-600 text-xs">{formError}</p>}

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 rounded-lg border border-white/50 dark:border-white/15 text-sm"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
                >
                  {saving ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
